// MA-707 — `column_add`: two-digit addition drawn as base-ten blocks in
// columns (ms.math.26, `itemType: "addition_regroup"`; since MA-704
// closure40 also ms.math.14's two-digit addition, via the mapper rule
// `columnAddWhereCountersRefused` — both addends 10–99, or a one-digit +
// two-digit pair whose sum is above 20; a one-digit addend's row is ones
// cubes beside an empty Tens cell, spoken "n ones", never "0 tens").
//
// WHAT IT DRAWS: a Tens column and a Ones column. The first addend is the
// top row, the second addend (with a "+") the bottom row, each as tens RODS
// in the Tens column and ones CUBES in the Ones column, labelled with the
// addend's own numeral card (the addends are read-the-prompt: the prompt is
// the bare equation "a + b = ?"). Under a rule line, the SUM row is EMPTY
// and IDENTICAL for every item: a "?" in the operator position and three
// empty dashed cells — an unlabelled one under the numeral-card column,
// then one under Tens and one under Ones. The third cell is there for
// every item, so the row's shape never depends on a or b (a digit-count-
// dependent row would tell the child whether the answer reaches a hundred
// — RED round 2 MF1, operator ruling closure39 round 3). Nothing depicts
// or labels the total, and no carried digit is drawn — the child does the
// regrouping.
//
// REUSE, NOT A FORK: rod/cube geometry, colours, the rect elements and the
// MA-658 aria grammar ("1 ten"/"3 tens", "1 one"/"7 ones") are imported from
// PlaceValueBlocksRenderer.tsx; the numeral cards are StagedMathRenderers'
// `numeralCard`.
//
// ANSWER-SAFETY BY CONSTRUCTION: this renderer reads ONLY `a` and `b` from
// its props — never `total` (the sum, carried on the payload for contract
// parity with every other add/sub kind). Pinned by a source grep in
// scripts/audit/columnAddIntegrityTest.mjs, and by that test's real-corpus
// markup/aria sweep over all 50 ms.math.26 items.
import type { MathRendererProps } from "./rendererRegistry";
import {
  U,
  STROKE,
  pushTensRods,
  pushOnesCubes,
  blockRectElements,
  placeCountPhrase,
  type BlockRect,
} from "./PlaceValueBlocksRenderer";
import { numeralCard } from "./StagedMathRenderers";

const TEXT = "var(--color-text-primary, #3d3228)";
const MUTED = "var(--color-text-secondary, #7a6f60)";
const COLUMN_BG = "var(--color-bg-secondary, #f5efe3)";

const CARD = 44; // addend / "?" numeral-card edge
const LABEL_W = 66; // "+" glyph + card
const CARD_X = 20;
const PAD = 6; // inner padding of a column cell
const MAX_PER_PLACE = 9; // a two-digit addend has at most 9 tens and 9 ones
const TENS_W = MAX_PER_PLACE * (U + 3) - 3 + PAD * 2; // room for 9 rods
const ONES_W = 44;
const TENS_X = LABEL_W + 6;
const ONES_X = TENS_X + TENS_W;
const HEAD = 20; // "Tens" / "Ones" header band
const ROW_H = 10 * U + PAD * 2; // a rod plus padding
const ANSWER_H = CARD + PAD * 2;
const RULE_GAP = 8;
const CELL_INSET = 3; // keeps the empty sum cells visibly separate
const WIDTH = ONES_X + ONES_W;

/** Row geometry, exported for the per-row block-count pin in
 *  scripts/audit/columnAddIntegrityTest.mjs (row 1 = y in [HEAD, HEAD+ROW_H)). */
export const COLUMN_ADD_LAYOUT = { HEAD, ROW_H } as const;

/** Tens/ones digits of an addend. Out-of-range input clamps to 0–99
 *  (fail-safe, never NaN) — the mapper emits 10–99, or (MA-704) 1–9 beside
 *  a two-digit addend when the sum is above 20, which gives tens 0. */
export function tensOnes(n: unknown): { tens: number; ones: number } {
  const v = Math.max(0, Math.min(99, Math.floor(Number.isFinite(n as number) ? (n as number) : 0)));
  return { tens: Math.floor(v / 10), ones: v % 10 };
}

/** "6 tens and 7 ones" — MA-658 singulars, and a zero place is left out
 *  rather than spoken: "2 tens" (not "and 0 ones"), and a one-digit addend
 *  (MA-704) is "4 ones", never "0 tens and 4 ones". Only an addend of 0 —
 *  never emitted — would read "0 ones". */
function rowPhrase(n: unknown): string {
  const { tens, ones } = tensOnes(n);
  const parts: string[] = [];
  if (tens > 0) parts.push(placeCountPhrase(tens, "ten", "tens"));
  if (ones > 0 || tens === 0) parts.push(placeCountPhrase(ones, "one", "ones"));
  return parts.join(" and ");
}

/** The picture's whole accessible name. Speaks each addend ROW's blocks
 *  only — never a composed numeral, never the sum. */
export function columnAddAriaLabel(a: unknown, b: unknown): string {
  return `Top row: ${rowPhrase(a)}. Bottom row: ${rowPhrase(b)}. Add them.`;
}

function ColumnAddRenderer({ a, b }: MathRendererProps) {
  const top = tensOnes(a);
  const bottom = tensOnes(b);
  const row1Y = HEAD;
  const row2Y = HEAD + ROW_H;
  const ruleY = row2Y + ROW_H + RULE_GAP / 2;
  const answerY = row2Y + ROW_H + RULE_GAP;
  const height = answerY + ANSWER_H;

  const rects: BlockRect[] = [];
  const cubeX = ONES_X + (ONES_W - U) / 2;
  for (const [digits, y] of [[top, row1Y], [bottom, row2Y]] as const) {
    pushTensRods(rects, TENS_X + PAD, digits.tens, y + PAD);
    pushOnesCubes(rects, cubeX, digits.ones, y + PAD);
  }

  const cardAt = (y: number, n: unknown, key: string) => (
    <g key={key} transform={`translate(0, ${y + (ROW_H - CARD) / 2})`}>{numeralCard(CARD_X, n, key, CARD)}</g>
  );

  return (
    <svg
      viewBox={`0 0 ${WIDTH + 2} ${height + 2}`}
      width="100%"
      style={{ maxWidth: 300 }}
      role="img"
      aria-label={columnAddAriaLabel(a, b)}
      data-testid="column-add"
    >
      {/* Column cells (tens | ones), spanning both addend rows. */}
      <rect x={TENS_X + 1} y={row1Y + 1} width={TENS_W} height={ROW_H * 2} fill={COLUMN_BG} stroke={STROKE} strokeWidth="1" />
      <rect x={ONES_X + 1} y={row1Y + 1} width={ONES_W} height={ROW_H * 2} fill={COLUMN_BG} stroke={STROKE} strokeWidth="1" />
      <line x1={TENS_X + 1} y1={row2Y + 1} x2={WIDTH + 1} y2={row2Y + 1} stroke={STROKE} strokeWidth="1" strokeDasharray="3 3" />
      <text x={TENS_X + TENS_W / 2 + 1} y={HEAD - 6} textAnchor="middle" fontSize="13" fontWeight="700" fill={MUTED}>Tens</text>
      <text x={ONES_X + ONES_W / 2 + 1} y={HEAD - 6} textAnchor="middle" fontSize="13" fontWeight="700" fill={MUTED}>Ones</text>
      {/* Addend rows: numeral card + the blocks. */}
      {cardAt(row1Y, a, "row-a")}
      <text x={9} y={row2Y + ROW_H / 2 + 8} textAnchor="middle" fontSize="24" fontWeight="700" fill={TEXT}>+</text>
      {cardAt(row2Y, b, "row-b")}
      {blockRectElements(rects, "blk-")}
      {/* The rule, then the EMPTY sum row: a "?" card and two empty cells. */}
      <line x1={0} y1={ruleY + 1} x2={WIDTH + 1} y2={ruleY + 1} stroke={TEXT} strokeWidth="2.5" />
      {/* The EMPTY answer row — the same for every item (never derived
          from a or b): "?" at the operator position, then three dashed
          cells. The first is unlabelled and simply stays blank when the
          answer has two digits. */}
      <text x={9} y={answerY + PAD + CARD / 2 + 8} textAnchor="middle" fontSize="24" fontWeight="700" fill={TEXT}>?</text>
      <rect data-testid="sum-cell" x={CARD_X + 2} y={answerY + PAD + 1} width={CARD - 4} height={CARD} rx="6" fill="none" stroke={STROKE} strokeWidth="1.5" strokeDasharray="5 4" />
      <rect data-testid="sum-cell" x={TENS_X + 1 + CELL_INSET} y={answerY + PAD + 1} width={TENS_W - CELL_INSET * 2} height={CARD} rx="6" fill="none" stroke={STROKE} strokeWidth="1.5" strokeDasharray="5 4" />
      <rect data-testid="sum-cell" x={ONES_X + 1 + CELL_INSET} y={answerY + PAD + 1} width={ONES_W - CELL_INSET * 2} height={CARD} rx="6" fill="none" stroke={STROKE} strokeWidth="1.5" strokeDasharray="5 4" />
    </svg>
  );
}

export default ColumnAddRenderer;
